import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  Typography,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { styled, useTheme } from "@mui/material/styles";
import { useGetListOfBugsQuery } from "@/features/bughurd/api";
import { typeToIconMapping } from "../utils";

// Styled components
const SearchContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const SearchResultsContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: 400,
  overflow: "auto",
  backgroundColor: theme.palette.background.paper,
  boxShadow: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: "none",
  borderRadius: "0 0 8px 8px",
  marginTop: 0,
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[600]
        : theme.palette.grey[300],
    borderRadius: 3,
  },
}));

const ResultItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  cursor: "pointer",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[800]
        : theme.palette.grey[50],
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

const IssueTypeChip = styled(Chip)<{ issuetype: string }>(
  ({ theme, issuetype }) => {
    const getTypeColor = (type: string) => {
      const isDark = theme.palette.mode === "dark";
      switch (type.toLowerCase()) {
        case "bug":
          return {
            bg: isDark ? "#331010" : "#FFEBEE",
            color: isDark ? "#FF6B6B" : "#D32F2F",
          };
        case "story":
          return {
            bg: isDark ? "#0F2A0F" : "#E8F5E8",
            color: isDark ? "#4CAF50" : "#388E3C",
          };
        case "epic":
          return {
            bg: isDark ? "#2A0F2A" : "#F3E5F5",
            color: isDark ? "#BA68C8" : "#7B1FA2",
          };
        default:
          return {
            bg: isDark ? "#2A2A2A" : "#F5F5F5",
            color: isDark ? "#BDBDBD" : "#616161",
          };
      }
    };

    const colors = getTypeColor(issuetype);

    return {
      height: 22,
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: 8,
      backgroundColor: colors.bg,
      color: colors.color,
      minWidth: "fit-content",
      "& .MuiChip-label": {
        paddingLeft: theme.spacing(0.75),
        paddingRight: theme.spacing(0.75),
      },
    };
  }
);

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  return {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 500,
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  };
});

// Custom hook for debounced value
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface TaskSearchProps {
  onSearch: (query: string) => void;
  onAddTask: (task: any) => void;
  placeholder?: string;
  selectedProject?: any;
}

const TaskSearch: React.FC<TaskSearchProps> = ({
  onSearch,
  onAddTask,
  placeholder,
  selectedProject,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null); // Add container ref
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Debounce the search value
  const debouncedSearchValue = useDebounce(searchQuery, 300);

  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    limit: 20,
    id: "",
    project: selectedProject ? [selectedProject._id] : [],
  });

  const shouldSkipQuery = !debouncedSearchValue.trim() || !selectedProject;

  const {
    data: listOfBugs,
    isLoading,
    isFetching,
  } = useGetListOfBugsQuery(filters, {
    skip: shouldSkipQuery,
  });

  // Reset search results and pagination when search value changes
  useEffect(() => {
    if (debouncedSearchValue !== filters.search) {
      setSearchResults([]);
      setCurrentPage(1);
      setHasMore(false);
      setTotalResults(0);
      setIsLoadingMore(false);

      if (debouncedSearchValue.trim() && selectedProject) {
        setFilters((prev) => ({
          ...prev,
          search: debouncedSearchValue,
          page: 1,
          project: [selectedProject._id],
        }));
        setShowResults(true);
      } else {
        setFilters((prev) => ({
          ...prev,
          search: "",
          page: 1,
          project: selectedProject ? [selectedProject._id] : [],
        }));
        setShowResults(false);
      }
    }
  }, [debouncedSearchValue, filters.search, selectedProject]);

  // Handle API response
  useEffect(() => {
    if (listOfBugs?.data?.decryptedData) {
      const newItems = listOfBugs.data.decryptedData.items || [];
      const totalCount = listOfBugs.data.decryptedData.total || 0;

      setTotalResults(totalCount);

      if (filters.page === 1) {
        setSearchResults(newItems);
      } else {
        setSearchResults((prev) => [...prev, ...newItems]);
        setIsLoadingMore(false);
      }

      const totalPages = Math.ceil(totalCount / filters.limit);
      const hasMoreData = filters.page < totalPages;
      setHasMore(hasMoreData);
    }
  }, [listOfBugs, filters.page, filters.limit]);

  // Load more data function
  const loadMore = useCallback(() => {
    if (
      !isLoadingMore &&
      hasMore &&
      !isFetching &&
      debouncedSearchValue.trim() &&
      selectedProject
    ) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      setFilters((prev) => ({
        ...prev,
        page: nextPage,
        project: [selectedProject._id],
      }));
    }
  }, [
    isLoadingMore,
    hasMore,
    isFetching,
    currentPage,
    debouncedSearchValue,
    selectedProject,
  ]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (loadMoreRef.current && hasMore && showResults) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const target = entries[0];
          if (target.isIntersecting) {
            loadMore();
          }
        },
        {
          threshold: 0.1,
          rootMargin: "20px",
        }
      );

      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, hasMore, showResults]);

  // Click outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if the click is outside the entire search container
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(target)
      ) {
        setShowResults(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowResults(false);
      }
    };

    // Use capture phase to ensure we catch the event before other handlers
    if (showResults) {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("keydown", handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [showResults]);

  const handleInputBlur = (event: React.FocusEvent) => {
    // Small delay to allow for clicks on search results
    setTimeout(() => {
      // Check if the new focus target is within our search container
      const activeElement = document.activeElement;
      if (
        searchContainerRef.current &&
        activeElement &&
        !searchContainerRef.current.contains(activeElement)
      ) {
        setShowResults(false);
      }
    }, 150);
  };

  const handleInputFocus = () => {
    if (searchQuery.trim() && (searchResults.length > 0 || isSearching)) {
      setShowResults(true);
    }
  };

  // Reset when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      setSearchQuery("");
      setShowResults(false);
      setSearchResults([]);
      setSelectedTasks(new Set());
      setCurrentPage(1);
      setHasMore(false);
      setTotalResults(0);
      setIsLoadingMore(false);
    }
  }, [selectedProject]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleTaskSelect = (task: any) => {
    const taskToAdd = {
      ...task,
      depType: "Waiting",
      status: "Waiting On",
      projectKey: selectedProject?.key || selectedProject?._id || "",
    };

    onAddTask(taskToAdd);

    setSelectedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(task._id)) {
        newSet.delete(task._id);
      } else {
        newSet.add(task._id);
      }
      return newSet;
    });

    // Optionally close results after selection (uncomment if desired)
    // setShowResults(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && searchQuery.trim()) {
      if (searchResults.length > 0) {
        handleTaskSelect(searchResults[0]);
      }
    }
  };

  const getIssueTypeIcon = (type: string) => {
    const typeInfo = type ? typeToIconMapping[type] : typeToIconMapping.TASK;
    return typeInfo;
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getAssigneeDisplay = (assignTo: any[]) => {
    if (!assignTo || assignTo.length === 0) return "Unassigned";
    const firstAssignee = assignTo[0];
    return firstAssignee.fullName || "Unknown";
  };

  const getSeverityColor = (severity: string) => {
    const isDark = theme.palette.mode === "dark";
    switch (severity?.toLowerCase()) {
      case "high":
        return {
          backgroundColor: isDark ? "#331010" : "#FFEBEE",
          color: isDark ? "#FF6B6B" : "#C62828",
        };
      case "medium":
        return {
          backgroundColor: isDark ? "#332010" : "#FFF3E0",
          color: isDark ? "#FFB74D" : "#EF6C00",
        };
      case "low":
        return {
          backgroundColor: isDark ? "#0F2A0F" : "#E8F5E8",
          color: isDark ? "#4CAF50" : "#2E7D32",
        };
      case "highest":
        return {
          backgroundColor: "#ff0000",
          color: "white",
        };
      default:
        return {
          backgroundColor: isDark ? "#2A2A2A" : "#F5F5F5",
          color: isDark ? "#BDBDBD" : "#616161",
        };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const isSearching = (isLoading || isFetching) && filters.page === 1;

  return (
    <SearchContainer ref={searchContainerRef}>
      <Box sx={{ p: 2 }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          placeholder={placeholder || t("Search by task number or task name")}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={!selectedProject}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#666", fontSize: 20 }} />
              </InputAdornment>
            ),
            // endAdornment: (
            //   <InputAdornment position="end">
            //     <IconButton
            //       size="small"
            //       onClick={() => {
            //         if (searchQuery.trim() && searchResults.length > 0) {
            //           handleTaskSelect(searchResults[0]);
            //         }
            //       }}
            //       disabled={!searchQuery.trim() || searchResults.length === 0}
            //       onMouseDown={(e) => e.preventDefault()} // Prevent input blur
            //     >
            //       <AddIcon sx={{ fontSize: 20 }} />
            //     </IconButton>
            //   </InputAdornment>
            // ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f8f9fa",
              borderRadius: showResults ? "8px 8px 0 0" : "8px",
              borderBottomColor: showResults
                ? "transparent"
                : theme.palette.divider,
            },
            "& .MuiInputBase-input": {
              fontSize: "0.95rem",
            },
          }}
        />

        {/* Search Results */}
        {showResults && (
          <Fade in={showResults}>
            <SearchResultsContainer
              ref={resultsContainerRef}
              onMouseDown={(e) => e.preventDefault()} // Prevent input blur on container
            >
              {/* Search Stats - Sticky Header */}
              {debouncedSearchValue.trim() && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    px: 2,
                    py: 1.5,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "grey.800" : "grey.50",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backdropFilter: "blur(8px)", // Optional: adds a subtle blur effect
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)", // Optional: adds subtle shadow for better separation
                  }}
                >
                  {/* Left Side - Instruction Text */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.85rem",
                      color: "primary.main",
                      flex: 1,
                    }}
                  >
                    {t("Select tasks that you are dependent on")}
                  </Typography>

                  {/* Right Side - Search Results Info */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      ml: 2,
                    }}
                  >
                    {isSearching ? (
                      <>
                        <CircularProgress size={12} sx={{ mr: 1 }} />
                        {t("Searching...")}
                      </>
                    ) : totalResults === 0 ? (
                      t("No results found")
                    ) : (
                      <>
                        {totalResults}{" "}
                        {totalResults === 1 ? t("task") : t("tasks")} found
                        {searchResults.length < totalResults && (
                          <span>
                            {" "}
                            • {t("Showing")} {searchResults.length}
                          </span>
                        )}
                      </>
                    )}
                  </Typography>
                </Box>
              )}

              <List sx={{ p: 0 }}>
                {/* Rest of your list items code remains the same */}
                {isSearching ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 3,
                    }}
                  >
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t("Searching...")}
                    </Typography>
                  </Box>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.map((task) => (
                      <ResultItem
                        key={task._id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleTaskSelect(task);
                        }}
                        onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                        sx={{
                          backgroundColor: selectedTasks.has(task._id)
                            ? theme.palette.mode === "dark"
                              ? theme.palette.primary.main + "20"
                              : theme.palette.primary.main + "08"
                            : "transparent",
                          borderLeft: selectedTasks.has(task._id)
                            ? `3px solid ${theme.palette.primary.main}`
                            : "3px solid transparent",
                        }}
                      >
                        {/* Your existing task item content */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "auto 1fr auto auto auto auto auto",
                            gap: 2,
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          {/* All your existing task item content remains the same */}
                          {/* Issue Type Icon */}
                          <Box
                            component="span"
                            sx={{
                              width: 16,
                              height: 16,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: task.type
                                ? typeToIconMapping[task.type]?.bgcolor
                                : typeToIconMapping.TASK.bgcolor,
                              borderRadius: "3px",
                              color: "white",
                              flexShrink: 0,
                            }}
                          >
                            {task.type
                              ? typeToIconMapping[task.type]?.icon
                              : typeToIconMapping.TASK.icon}
                          </Box>

                          {/* Task Key and Title */}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "0.9rem",
                              }}
                            >
                              {task.bugKey} - {task.summary}
                            </Typography>
                          </Box>

                          {/* Type Chip */}
                          <Box>
                            <IssueTypeChip
                              label={task.type}
                              size="small"
                              issuetype={task.type}
                            />
                          </Box>

                          {/* Status Chip */}
                          <Box>
                            <StatusChip
                              label={task?.bugStatus?.status}
                              size="small"
                              status={task?.bugStatus?.status}
                            />
                          </Box>

                          {/* Priority Chip */}
                          {task.severity && (
                            <Box>
                              <Chip
                                label={task.severity}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.75rem",
                                  ...getSeverityColor(task.severity),
                                }}
                              />
                            </Box>
                          )}

                          {/* Assignee Avatar Only */}
                          <Box>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: "0.7rem",
                                backgroundColor: "primary.main",
                              }}
                            >
                              {getInitials(getAssigneeDisplay(task.assignTo))}
                            </Avatar>
                          </Box>

                          {/* Date */}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{
                                fontSize: "0.8rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDate(task.updatedAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </ResultItem>
                    ))}

                    {/* Load More Indicator */}
                    {hasMore && (
                      <Box
                        ref={loadMoreRef}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          {t("Loading more...")}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : debouncedSearchValue.trim() && !isSearching ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 3,
                      color: "text.secondary",
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 32, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">
                      {t("No tasks found")}
                    </Typography>
                  </Box>
                ) : null}
              </List>
            </SearchResultsContainer>
          </Fade>
        )}
      </Box>
    </SearchContainer>
  );
};

export default TaskSearch;
